import type { ArgoCDApplicationResource } from "@octopusdeploy/octopus-server-client";
import { ScriptingLanguage, ArgoCDAnnotationKeys } from "@octopusdeploy/octopus-server-client";
import type { ConfiguredSources } from "./ConfiguredSource";

export type ArgoCDAnnotationsFormat = "YAML" | "ARGOCD" | "KUBECTL";

export function createAnnotation(configuredSources: ConfiguredSources): string {
    const annotations: Partial<Record<string, string | undefined>>[] = Object.values(configuredSources).map(({ source, projectSlug, environmentSlug, tenantSlug, helmExpression }) => ({
        [ArgoCDAnnotationKeys.OctopusProjectAnnotationKey(source.Name)]: projectSlug,
        [ArgoCDAnnotationKeys.OctopusEnvironmentAnnotationKey(source.Name)]: environmentSlug,
        [ArgoCDAnnotationKeys.OctopusTenantAnnotationKey(source.Name)]: tenantSlug,
        [ArgoCDAnnotationKeys.OctopusImageReplacementPathsKey(source.Name)]: helmExpression,
    }));

    let annotation = `metadata:
  annotations:`;

    for (const dict of annotations) {
        for (const [key, value] of Object.entries(dict)) {
            if (value) {
                annotation += `\n    ${key}: "${value}"`;
            }
        }
    }

    return annotation;
}

export function verifyAnnotations(application: ArgoCDApplicationResource, configuredSources: ConfiguredSources): boolean {
    for (const expectedSource of Object.values(configuredSources)) {
        const projectAnnotation = application.Annotations[ArgoCDAnnotationKeys.OctopusProjectAnnotationKey(expectedSource.source.Name)];
        if (!projectAnnotation || projectAnnotation !== expectedSource.projectSlug) {
            return false;
        }

        const environmentAnnotation = application.Annotations[ArgoCDAnnotationKeys.OctopusEnvironmentAnnotationKey(expectedSource.source.Name)];
        if (!environmentAnnotation || environmentAnnotation !== expectedSource.environmentSlug) {
            return false;
        }

        const tenantAnnotation = application.Annotations[ArgoCDAnnotationKeys.OctopusTenantAnnotationKey(expectedSource.source.Name)];
        if (tenantAnnotation !== expectedSource.tenantSlug) {
            return false;
        }

        const helmExpressionAnnotation = application.Annotations[ArgoCDAnnotationKeys.OctopusImageReplacementPathsKey(expectedSource.source.Name)];
        if (helmExpressionAnnotation !== expectedSource.helmExpression) {
            return false;
        }
    }

    return true;
}

export function createCliAnnotationsPatchCommand(format: ArgoCDAnnotationsFormat, syntax: ScriptingLanguage, configuredSources: ConfiguredSources, application?: ArgoCDApplicationResource): string {
    const linebreakChar = syntax === ScriptingLanguage.Bash ? "\\\n" : "`\n";
    const annotations = getAnnotationsAsJson(configuredSources);

    if (format === "ARGOCD") {
        return createArgoCDCliAnnotationsPatchCommand(linebreakChar, annotations, application?.Name, application?.KubernetesNamespace);
    }

    if (format === "KUBECTL") {
        return createKubectlCliAnnotationsPatchCommand(linebreakChar, annotations, application?.Name, application?.KubernetesNamespace);
    }

    return "";
}

function createArgoCDCliAnnotationsPatchCommand(linebreakChar: string, jsonAnnotations: string, applicationName?: string, applicationNamespace?: string): string {
    return `argocd app patch ${applicationName ?? ""} ${linebreakChar} --app-namespace ${applicationNamespace ?? ""} ${linebreakChar} --patch '${jsonAnnotations}' ${linebreakChar} --type merge`;
}

function createKubectlCliAnnotationsPatchCommand(linebreakChar: string, jsonAnnotations: string, applicationName?: string, applicationNamespace?: string): string {
    return `kubectl patch application ${applicationName ?? ""} ${linebreakChar} -n ${applicationNamespace ?? ""} ${linebreakChar} -p '${jsonAnnotations}' ${linebreakChar} --type='merge'`;
}

function getAnnotationsAsJson(configuredSources: ConfiguredSources) {
    const annotationValues: Partial<Record<string, string | undefined>>[] = Object.values(configuredSources).map(({ source, projectSlug, environmentSlug, tenantSlug, helmExpression }) => ({
        [ArgoCDAnnotationKeys.OctopusProjectAnnotationKey(source.Name)]: projectSlug,
        [ArgoCDAnnotationKeys.OctopusEnvironmentAnnotationKey(source.Name)]: environmentSlug,
        [ArgoCDAnnotationKeys.OctopusTenantAnnotationKey(source.Name)]: tenantSlug,
        [ArgoCDAnnotationKeys.OctopusImageReplacementPathsKey(source.Name)]: helmExpression,
    }));

    return JSON.stringify({
        metadata: {
            annotations: Object.assign({}, ...annotationValues),
        },
    });
}
